import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { ArrowLeft, Mail, KeyRound, Check, Loader2, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { ISSUES, emojiFor } from "@/data/issues";
import { US_STATES } from "@/data/states";
import VoiceCard from "@/components/VoiceCard";
import SiteFooter from "@/components/SiteFooter";
import "@/voteforme.css";

const LOGO_WHITE = "/sbgwc-logo-white.png";
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export default function EditPledge() {
  const [params] = useSearchParams();
  const [step, setStep] = useState("email"); // email | code | pick | form | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const [slugStatus, setSlugStatus] = useState(null);
  const cardRef = useRef(null);
  const exportRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pledges/edit/request", { email });
      toast.success("Check your inbox for a 6-digit code");
      setStep("code");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send a code"); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pledges/edit/verify", { email, code });
      setToken(data.token); setPledges(data.pledges);
      const want = params.get("slug");
      const match = data.pledges.find((x) => x.slug === want) || (data.pledges.length === 1 ? data.pledges[0] : null);
      if (match) choose(match); else setStep("pick");
    } catch (err) { toast.error(err?.response?.data?.detail || "That code didn't work"); }
    finally { setBusy(false); }
  };

  const choose = (pl) => {
    setP(pl);
    setForm({ name: pl.name, age: pl.age || "", city: pl.city || "", state: pl.state || "", issues: pl.issues, custom_message: pl.custom_message || "", slug: pl.slug });
    setStep("form");
  };

  const toggleIssue = (id) => setForm((f) => ({ ...f, issues: f.issues.includes(id) ? f.issues.filter((x) => x !== id) : f.issues.length >= 6 ? f.issues : [...f.issues, id] }));

  const checkSlug = async () => {
    const s = slugify(form.slug);
    if (!s || s === p.slug) { setSlugStatus(null); return; }
    try { const { data } = await api.get(`/pledges/slug-available/${s}`); setSlugStatus(data.available ? "ok" : "taken"); } catch (err) { console.warn("Slug check failed", err); setSlugStatus(null); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.issues.length === 0) { toast.error("Pick at least one issue"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/pledges/${p.slug}`, { ...form, slug: slugify(form.slug) || p.slug }, auth);
      setP(data);
      await new Promise((r) => setTimeout(r, 350));
      try {
        const canvas = await html2canvas(exportRef.current || cardRef.current, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: "#3A0300", logging: false });
        const blob = await (await fetch(canvas.toDataURL("image/png"))).blob();
        const fd = new FormData(); fd.append("file", blob, "voice-card.png");
        await api.post(`/pledges/${data.slug}/voice-card`, fd, auth);
      } catch (err) { console.warn("Card regeneration failed", err); toast.error("Saved, but the card image couldn't be refreshed — try 'Edit again'."); }
      setStep("done");
      toast.success("Your pledge is updated ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save changes"); }
    finally { setBusy(false); }
  };

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  return (
    <div className="vfm"><div className="vfm-bg-glow" />
      <div className="champion-page edit-page" data-testid="edit-page">
        <Link to="/" className="back-link" data-testid="edit-back-home"><ArrowLeft size={14} /> Home</Link>
        <img className="champion-logo" src={LOGO_WHITE} alt="SBGWC" />
        <div className="champion-eyebrow">Edit your pledge</div>

        {step === "email" && (
          <form className="edit-box" onSubmit={requestCode} data-testid="edit-step-email">
            <h1 className="champion-heading" style={{ fontSize: 30 }}>Let's find your card</h1>
            <p className="champion-sub">Enter the email you used when you made your pledge and we'll send you a 6-digit code.</p>
            <div className="input-group" style={{ width: "100%" }}>
              <label className="input-label">Email</label>
              <input type="email" className="text-input" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="edit-email-input" />
            </div>
            <button type="submit" className="btn-yellow" disabled={busy || !email.trim()} data-testid="edit-send-code-btn"><Mail size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />{busy ? "Sending…" : "Send my code"}</button>
            <p className="edit-hint">Didn't add an email when you pledged? Then there's no way to verify it's you — but you can always <Link to="/">make a fresh card</Link>.</p>
          </form>
        )}

        {step === "code" && (
          <form className="edit-box" onSubmit={verify} data-testid="edit-step-code">
            <h1 className="champion-heading" style={{ fontSize: 30 }}>Enter your code</h1>
            <p className="champion-sub">We sent a 6-digit code to <b>{email}</b>. It's good for 15 minutes.</p>
            <input className="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" data-testid="edit-code-input" />
            <button type="submit" className="btn-yellow" disabled={busy || code.length !== 6} data-testid="edit-verify-btn"><KeyRound size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />{busy ? "Checking…" : "Verify"}</button>
            <button type="button" className="btn-secondary" onClick={() => setStep("email")} data-testid="edit-resend-btn">Use a different email / resend</button>
          </form>
        )}

        {step === "pick" && (
          <div className="edit-box" data-testid="edit-step-pick">
            <h1 className="champion-heading" style={{ fontSize: 30 }}>Which card?</h1>
            <p className="champion-sub">We found {pledges.length} pledges with this email.</p>
            {pledges.map((pl) => (
              <button key={pl.slug} className="pick-row" onClick={() => choose(pl)} data-testid={`edit-pick-${pl.slug}`}>
                <span><b>{pl.name}</b> · /p/{pl.slug}</span><span className="pick-meta"><Eye size={12} /> {pl.views}</span>
              </button>
            ))}
          </div>
        )}

        {step === "form" && form && (
          <div className="edit-box wide" data-testid="edit-step-form">
            <h1 className="champion-heading" style={{ fontSize: 30 }}>Update your pledge</h1>
            <div className="input-row">
              <div className="input-group" style={{ flex: 2 }}><label className="input-label">Name</label><input className="text-input" value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name-input" /></div>
              <div className="input-group" style={{ flex: 1 }}><label className="input-label">Age</label><input className="text-input" value={form.age} maxLength={2} onChange={(e) => setForm({ ...form, age: e.target.value.replace(/\D/g, "") })} data-testid="edit-age-input" /></div>
            </div>
            <div className="input-row">
              <div className="input-group" style={{ flex: 2 }}><label className="input-label">City</label><input className="text-input" value={form.city} maxLength={60} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-city-input" /></div>
              <div className="input-group" style={{ flex: 1 }}><label className="input-label">State</label>
                <select className="text-input select-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="edit-state-select"><option value="">—</option>{US_STATES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select></div>
            </div>
            <div className="input-group" style={{ width: "100%" }}>
              <label className="input-label">Your issues <span className="lbl-opt">({form.issues.length}/6)</span></label>
              <div className="chip-grid" data-testid="edit-issues">
                {ISSUES.map((i) => (
                  <button key={i.id} type="button" className={`chip ${form.issues.includes(i.id) ? "on" : ""}`} onClick={() => toggleIssue(i.id)} data-testid={`edit-issue-${i.id}`}>{i.emoji} {i.name}</button>
                ))}
              </div>
            </div>
            <div className="input-group" style={{ width: "100%" }}>
              <label className="input-label">My personal message to you <span className="lbl-opt">({160 - form.custom_message.length} left)</span></label>
              <textarea className="text-input text-area" rows={2} maxLength={160} value={form.custom_message} onChange={(e) => setForm({ ...form, custom_message: e.target.value })} data-testid="edit-custom-input" />
            </div>
            <div className="slug-field" style={{ maxWidth: "none" }}>
              <label className="input-label">Personal link</label>
              <div className="slug-input-wrap"><span className="slug-prefix">{window.location.host}/p/</span>
                <input className="slug-input" value={form.slug} onChange={(e) => { setForm({ ...form, slug: slugify(e.target.value) }); setSlugStatus(null); }} onBlur={checkSlug} data-testid="edit-slug-input" /></div>
              {slugStatus === "ok" && <div className="slug-status ok">✓ Available</div>}
              {slugStatus === "taken" && <div className="slug-status taken">Taken — try another</div>}
              {form.slug !== p.slug && <div className="edit-hint" style={{ textAlign: "left", marginTop: 6 }}>Your old link /p/{p.slug} will keep working and redirect here.</div>}
            </div>
            <div className="pledge-preview-wrap" style={{ width: "100%" }}>
              <VoiceCard ref={cardRef} name={form.name} age={form.age} issues={form.issues} custom={form.custom_message} city={form.city} state={form.state} />
            </div>
            <button className="btn-yellow" onClick={save} disabled={busy || slugStatus === "taken"} data-testid="edit-save-btn">{busy ? <><Loader2 size={15} className="spin" style={{ marginRight: 6, verticalAlign: "-2px", animation: "spin 1s linear infinite" }} />Saving…</> : <><Check size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />Save changes</>}</button>
          </div>
        )}

        {step === "done" && p && (
          <div className="edit-box" data-testid="edit-step-done">
            <h1 className="champion-heading" style={{ fontSize: 30 }}>All updated, {p.name.split(" ")[0]}!</h1>
            <p className="champion-sub">Your card and page are refreshed. We've emailed you the new version too.</p>
            <div className="link-box" style={{ width: "100%" }}><code data-testid="edit-done-link">{window.location.origin}/p/{p.slug}</code></div>
            <Link to={`/p/${p.slug}`} className="btn-yellow" style={{ textDecoration: "none", textAlign: "center" }} data-testid="edit-view-page-btn">See my page →</Link>
            <button className="btn-secondary" onClick={() => choose(p)} data-testid="edit-again-btn">Edit again</button>
          </div>
        )}
      </div>
      {form && (
        <div className="vc-offscreen" aria-hidden="true" ref={exportRef}>
          <VoiceCard name={form.name} age={form.age} issues={form.issues} custom={form.custom_message} city={form.city} state={form.state} />
        </div>
      )}
      <SiteFooter />
    </div>
  );
}
